/**
 * Encounter Library Types
 *
 * Single-session encounter templates — distinct from modules/SessionPlans
 * which are multi-session arcs. An encounter is a reusable setpiece:
 * map + mob loadout + environmental hazards + optional round-based surprises.
 *
 * A GM can deploy an encounter into any active campaign/scene to stage a fight.
 */
import { z } from 'zod';
import { BestiaryActionSchema, AbilitiesSchema } from './bestiary-types';
// =============================================================================
// MAP REFERENCE
// =============================================================================
export const EncounterMapRefSchema = z.object({
    type: z.enum(['generate', 'saved', 'static']),
    savedMapId: z.string().uuid().nullable().optional(),
    staticUrl: z.string().nullable().optional(),
    prompt: z.string().nullable().optional(),
    environment: z.string().nullable().optional(),
    mood: z.string().nullable().optional(),
    style: z.string().nullable().optional(),
    widthFeet: z.number().default(80),
    heightFeet: z.number().default(60),
    mapName: z.string().nullable().optional(),
    isoHint: z.string().nullable().optional(),
});
// =============================================================================
// MOB SPEC — what to spawn
// =============================================================================
export const EncounterMobPlacementSchema = z.union([
    z.literal('random'),
    z.literal('rear'),
    z.literal('ahead'),
    z.literal('flanking'),
    z.literal('hidden'),
    z.object({ x: z.number(), y: z.number() }),
]);
export const EncounterMobSpecSchema = z.object({
    /** Reference to machina_bestiary (preferred) */
    bestiaryId: z.string().uuid().nullable().optional(),
    /** Open5e slug to auto-import if no bestiaryId */
    open5eSlug: z.string().nullable().optional(),
    /** Display name (falls back to bestiary entry name) */
    name: z.string(),
    count: z.number().default(1),
    /** Ad-hoc stat override (if no bestiaryId) */
    ac: z.number().nullable().optional(),
    maxHp: z.number().nullable().optional(),
    abilities: AbilitiesSchema.optional(),
    actions: z.array(BestiaryActionSchema).default([]),
    cr: z.string().nullable().optional(),
    size: z.string().nullable().optional(),
    creatureType: z.string().nullable().optional(),
    /** Placement hint */
    placement: EncounterMobPlacementSchema.optional(),
    /** Image URL / generation hint */
    imgUrl: z.string().nullable().optional(),
    imgPrompt: z.string().nullable().optional(),
    /** Start with hidden/invisible condition */
    hidden: z.boolean().default(false),
    /** GM tactical notes for this unit */
    notes: z.string().nullable().optional(),
});
// =============================================================================
// HAZARDS — environmental pressure
// =============================================================================
export const HazardTypeSchema = z.enum([
    'lava',
    'fire',
    'spike_growth',
    'poison_cloud',
    'cover',
    'difficult_terrain',
    'cliff_edge',
    'signal_fire',
    'custom',
]);
export const EncounterHazardSchema = z.object({
    id: z.string(),
    type: HazardTypeSchema,
    name: z.string(),
    description: z.string(),
    /** Damage dice if creature enters/fails save (e.g. "2d6") */
    damage: z.string().nullable().optional(),
    damageType: z.string().nullable().optional(),
    /** Save to avoid / halve damage */
    saveDC: z.number().nullable().optional(),
    saveAbility: z.string().nullable().optional(),
    /** Optional board footprint (in grid squares) */
    bounds: z.object({
        x: z.number(),
        y: z.number(),
        w: z.number(),
        h: z.number(),
    }).nullable().optional(),
    /** Active at start, or activated by a trigger */
    activeAtStart: z.boolean().default(true),
});
// =============================================================================
// TRIGGERS — mid-battle surprises
// =============================================================================
export const TriggerConditionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('round'), round: z.number() }),
    z.object({ type: z.literal('mob_hp_pct'), mobNameIncludes: z.string(), thresholdPct: z.number() }),
    z.object({ type: z.literal('mob_count_remaining'), mobNameIncludes: z.string().nullable().optional(), threshold: z.number() }),
    z.object({ type: z.literal('objective_failed'), objectiveId: z.string() }),
    z.object({ type: z.literal('objective_succeeded'), objectiveId: z.string() }),
    z.object({ type: z.literal('manual') }),
]);
export const TriggerActionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('narration'),
        text: z.string(),
        chatRole: z.enum(['gm', 'system']).default('gm'),
    }),
    z.object({
        type: z.literal('spawn_mobs'),
        mobs: z.array(EncounterMobSpecSchema),
        narration: z.string().nullable().optional(),
        /** Optional surprise attack — new mobs get advantage on their first round */
        surprise: z.boolean().default(false),
    }),
    z.object({
        type: z.literal('activate_hazard'),
        hazardId: z.string(),
        narration: z.string().nullable().optional(),
    }),
    z.object({
        type: z.literal('env_change'),
        description: z.string(),
    }),
]);
export const EncounterTriggerSchema = z.object({
    id: z.string(),
    name: z.string(),
    condition: TriggerConditionSchema,
    action: TriggerActionSchema,
    /** Runtime only — set true when fired */
    fired: z.boolean().default(false),
});
// =============================================================================
// OBJECTIVES — optional win/fail conditions shown to GM
// =============================================================================
export const EncounterObjectiveSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    /** Rounds allowed before this fails (if any) */
    timerRounds: z.number().nullable().optional(),
    /** GM-only note about what happens on success/failure */
    onSuccess: z.string().nullable().optional(),
    onFail: z.string().nullable().optional(),
});
// =============================================================================
// ENCOUNTER TEMPLATE — the top-level entity
// =============================================================================
export const EncounterDifficultySchema = z.enum(['easy', 'medium', 'hard', 'deadly']);
export const EncounterTemplateSchema = z.object({
    id: z.string().uuid(),
    /** Null for built-in/system templates */
    campaignId: z.string().uuid().nullable().optional(),
    slug: z.string().nullable().optional(),
    name: z.string(),
    subtitle: z.string().nullable().optional(),
    description: z.string(),
    flavorText: z.string().nullable().optional(),
    environment: z.string(),
    difficulty: EncounterDifficultySchema.default('medium'),
    recommendedLevel: z.string().default('1-5'),
    partySize: z.number().default(4),
    map: EncounterMapRefSchema,
    mobs: z.array(EncounterMobSpecSchema).default([]),
    hazards: z.array(EncounterHazardSchema).default([]),
    triggers: z.array(EncounterTriggerSchema).default([]),
    objectives: z.array(EncounterObjectiveSchema).default([]),
    setupNotes: z.string().nullable().optional(),
    tacticalNotes: z.string().nullable().optional(),
    rewardNotes: z.string().nullable().optional(),
    tags: z.array(z.string()).default([]),
    /** Hint that map/mobs should be rendered iso (HD-2D) */
    isoFriendly: z.boolean().default(false),
    source: z.string().default('custom'),
    createdAt: z.string(),
    updatedAt: z.string(),
});
//# sourceMappingURL=encounter-library-types.js.map